import { SidebarContextWrapper, useSidebar } from "@/components/ui";
import { Textarea } from "@/components/ui/textarea";
import { TEST_IDS } from "@/constants/testIds";
import { useCanvas } from "@/contexts/CanvasContext";
import { useChat } from "@/contexts/ChatContext";
import { useIsMobile, useMediaQuery, useToast } from "@/hooks";
import { cn } from "@/lib";
import { useFileStorageStore } from "@/store";
import useAssistantStore from "@/store/assistantStore";
import useAuthStore from "@/store/authStore";
import useThreadStore from "@/store/threadStore";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ChatGreeting,
  DragDropOverlay,
  FileMentionDropdown,
  FilePreviewList,
  FileUploadButton,
  ImagePreviewDialog,
  ImageStyleSelector,
  InlineImageModelSelect,
  InlineImageModelParams,
  InputTokenCounter,
  ReplyContextBar,
  SendButton,
  SimpleToolBox,
  VoiceDictationButton,
  WebSearchFocus,
} from "./components";
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILES,
  dragDropAnimationStyle,
  fadeAnimationStyle,
} from "./constants";
import {
  useDragDrop,
  useFileMention,
  useFileUpload,
  useInputHandling,
  useItemMentionSelection,
  useKeyboardHandling,
  useMessageSubmission,
  useReferencedItems,
  useTextareaResize,
} from "./hooks";

// Production ChatInput component with optimized file handling

// Create a style element once and reuse it
let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected) return;
  const styleElement = document.createElement("style");
  styleElement.id = "debug-chat-input-styles";
  styleElement.textContent = `
    ${fadeAnimationStyle}
    ${dragDropAnimationStyle}
    .chat-input-container.has-files {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }
  `;
  document.head.appendChild(styleElement);
  stylesInjected = true;
};

/**
 * ChatInput component - Production version with optimized file handling
 * Stable file state management and improved performance
 */
export function ChatInput({
  onSendMessage,
  placeholder = "Type a message...",
  maxFiles = MAX_FILES,
  isNewChat = false,
  autoFocus = false,
  disableTools = false,
  disableFileUpload = false,
  disableVoice = false,
  isLimitedMode = false,
  onShowAuthOverlay,
}) {
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Sidebar-aware narrow toolbar detection
  // Mobile (< 768px): sidebar overlays, doesn't affect chat width
  // Desktop expanded: sidebar takes ~248px
  // Desktop collapsed: sidebar takes ~48px
  const { state: sidebarState } = useSidebar();
  const isNarrowMobile = useMediaQuery("(max-width: 815px)");
  const isNarrowWithExpandedSidebar = useMediaQuery("(max-width: 1055px)");
  const isNarrowWithCollapsedSidebar = useMediaQuery("(max-width: 865px)");
  const isNarrowToolbar = isMobile
    ? isNarrowMobile
    : sidebarState === "expanded"
      ? isNarrowWithExpandedSidebar
      : isNarrowWithCollapsedSidebar;
  const userId = useAuthStore((state) => state.userId);
  const { isCanvasOpen } = useCanvas();
  const chatContext = useChat();
  const location = useLocation();

  // Extract deep research state from chat context
  const isDeepResearchEnabled = chatContext?.isDeepResearchEnabled || false;
  const isDeepResearchInProgress =
    chatContext?.isDeepResearchInProgress || false;
  const isCanvasModeEnabled = chatContext?.isCanvasModeEnabled || false;

  // Extract reply state from chat context
  const replyToMessage = chatContext?.replyToMessage || null;
  const cancelReplyToMessage = chatContext?.cancelReplyToMessage || (() => {});

  // Thread and model state
  const threadIsLoading = useThreadStore((state) => state.isLoading);
  const threadIsStreaming = useThreadStore((state) => state.isStreaming);
  const threadAbortController = useThreadStore(
    (state) => state.abortController
  );
  const hasStaleStreamingState = useThreadStore(
    (state) => state.hasStaleStreamingState
  );
  const getOptimisticMessages = useThreadStore(
    (state) => state.getOptimisticMessages
  );
  const threadCurrentId = useThreadStore((state) => state.currentThreadId);
  const metadata = useThreadStore((state) => state.metadata);
  const threadMessages = useThreadStore((state) => state.messages);
  const fileStorageStore = useFileStorageStore();
  const getCurrentAssistantModelConfig = useAssistantStore(
    (state) => state.getCurrentAssistantModelConfig
  );

  // UI state
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const [message, setMessage] = useState("");
  const [activeToolId, setActiveToolId] = useState(null);
  const messageBeforeVoiceRef = useRef("");
  const [styleData, setStyleData] = useState(null);

  // Translation function for components that need i18n
  // WebSearchFocus component has built-in English translations as fallback
  // Return null to indicate no translation available, component will use built-in translations
  const safeT = null;

  const isLoading = threadIsLoading;

  // Check for optimistic user messages (pending request before streaming starts)
  const optimisticMessages = threadCurrentId
    ? getOptimisticMessages?.(threadCurrentId) || []
    : [];
  const hasOptimisticUserMessage = optimisticMessages.some(
    (msg) => msg.isOptimistic && msg.role === "user"
  );

  // Detect stale streaming state (isStreaming persisted but abortController lost after reload)
  const isStaleStreaming = hasStaleStreamingState
    ? hasStaleStreamingState()
    : false;

  // Show stop button if:
  // 1. Streaming is active (has abortController), OR
  // 2. There's an optimistic user message (request pending, before streaming starts), OR
  // 3. There's a stale streaming state (after reload - no abortController but isStreaming is true)
  const isStreaming =
    (threadIsStreaming && !!threadAbortController) ||
    hasOptimisticUserMessage ||
    isStaleStreaming;
  const currentThreadId = threadCurrentId;

  const dynamicPlaceholder = isDeepResearchInProgress
    ? "Deep research in progress... Please wait"
    : isCanvasOpen
      ? "Ask Anything"
      : isMobile
        ? "Ask anything"
        : placeholder;

  // Refs
  const textareaRef = useRef(null);
  const currentWorkspaceId = useAuthStore((state) => state.currentWorkspaceId);

  // Calculate total tokens
  const totalTokens = useMemo(() => {
    const config = getCurrentAssistantModelConfig();
    return config?.tokens || 16000;
  }, [getCurrentAssistantModelConfig]);

  // Get caret coordinates for mention dropdown
  const getCaretCoordinates = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return null;
    const textareaRect = textarea.getBoundingClientRect();
    return {
      left: textareaRect.left + 20,
      top: textareaRect.top + 30,
    };
  }, []);

  // Initialize all custom hooks (reuse existing)
  const {
    referencedItems,
    addReferencedItem,
    removeReferencedItem,
    clearReferencedItems,
  } = useReferencedItems();

  const {
    mentionDropdownOpen,
    setMentionDropdownOpen,
    mentionTriggerPos,
    setMentionTriggerPos,
    filteredItems,
    workspaceFiles,
    workspaceMemories,
    selectedIndex,
    setSelectedIndex,
    mentionStartRef,
    handleMentionDetection,
    forceTriggerMentionDropdown,
    handleMentionKeyDown,
  } = useFileMention(currentWorkspaceId, toast);

  const {
    uploadedFiles: files,
    setUploadedFiles: setFiles,
    storageFiles,
    setStorageFiles,
    isUploading,
    setIsUploading,
    fileInputRef,
    handleFileChange,
    handlePaste,
    handleRemoveFile,
  } = useFileUpload(currentThreadId, maxFiles, toast);

  const { isFocused, setIsFocused, inputTokens, handleInputChange } =
    useInputHandling(
      message,
      setMessage,
      getCaretCoordinates,
      handleMentionDetection,
      setMentionTriggerPos,
      setMentionDropdownOpen,
      toast,
      threadMessages
    );

  const { handleItemMentionSelect } = useItemMentionSelection(
    textareaRef,
    message,
    setMessage,
    mentionStartRef,
    setMentionDropdownOpen,
    workspaceMemories,
    setFiles,
    setStorageFiles,
    setIsUploading,
    currentThreadId,
    toast
  );

  const { handleSubmit } = useMessageSubmission(
    message,
    setMessage,
    files,
    setFiles,
    storageFiles,
    setStorageFiles,
    referencedItems,
    clearReferencedItems,
    isUploading,
    isLoading,
    currentThreadId,
    isDeepResearchEnabled, // Use the actual value from chat context
    onSendMessage,
    fileStorageStore,
    replyToMessage,
    cancelReplyToMessage
  );

  // Safari iOS viewport nudge: blur + scroll to help Safari recalculate viewport after keyboard closes
  const nudgeSafariViewport = useCallback(() => {
    // Only apply on iOS Safari
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (!isIOS) return;

    // Blur to close keyboard
    textareaRef.current?.blur();

    // Nudge scroll to encourage viewport recalculation
    requestAnimationFrame(() => {
      window.scrollTo(window.scrollX, window.scrollY + 1);
      requestAnimationFrame(() => {
        window.scrollTo(window.scrollX, window.scrollY - 1);
      });
    });
  }, []);

  // Wrapped submit handler that includes Safari viewport nudge
  const handleSubmitWithNudge = useCallback(
    (e, options) => {
      handleSubmit(e, options);
      // Nudge Safari viewport after a brief delay to let the submit process start
      setTimeout(nudgeSafariViewport, 100);
    },
    [handleSubmit, nudgeSafariViewport]
  );

  const { handleKeyDown } = useKeyboardHandling(
    textareaRef,
    message,
    handleMentionKeyDown,
    filteredItems,
    handleItemMentionSelect,
    (e) =>
      handleSubmitWithNudge(e, {
        forceWebSearchTool: activeToolId === "web_search",
        forceImageGenTool: activeToolId === "image_gen",
        hasExplicitToolSelection: activeToolId !== null,
        tool_choice: activeToolId ? undefined : "none",
      }),
    forceTriggerMentionDropdown
  );

  const {
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragDrop(async (droppedFiles) => {
    await handleFileChange({ target: { files: droppedFiles } });
  });

  // Pass activeToolId to trigger resize when toolbar layout changes (two-row ↔ single-row)
  const textareaHeight = useTextareaResize(
    message,
    textareaRef,
    isMobile,
    activeToolId
  );

  // Control greeting visibility - only show on new chat pages
  const showGreeting = isNewChat;

  // Use a stable key for greeting that only changes when actually starting new chats
  const [greetingKey, setGreetingKey] = useState(0);

  // Use a global ref to ensure only one greeting update per new chat session
  const greetingUpdatedForSessionRef = useRef(false);

  // Reset global flag when navigating away from /chat/new
  useEffect(() => {
    if (!isNewChat) {
      greetingUpdatedForSessionRef.current = false;
    }
  }, [isNewChat]);

  // Listen for new chat started events to refresh greetings (globally unique)
  useEffect(() => {
    const handleNewChatStarted = () => {
      // Only update greeting once globally per new chat session
      if (isNewChat && !greetingUpdatedForSessionRef.current) {
        greetingUpdatedForSessionRef.current = true;
        setGreetingKey((prev) => prev + 1);
      }
    };

    window.addEventListener("new-chat-started", handleNewChatStarted);

    return () => {
      window.removeEventListener("new-chat-started", handleNewChatStarted);
    };
  }, [isNewChat]);

  // Auto-focus textarea when on new chat page or when autoFocus prop is true
  useEffect(() => {
    if ((isNewChat || autoFocus) && textareaRef.current) {
      // Small delay to ensure the component is fully rendered
      const timeoutId = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);

      // Also focus when new-chat-started event fires (user clicks New Chat while already on /chat/new)
      const handleFocusOnNewChat = () => {
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      };

      window.addEventListener("new-chat-started", handleFocusOnNewChat);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("new-chat-started", handleFocusOnNewChat);
      };
    }
  }, [isNewChat, autoFocus]);

  // Inject styles
  useEffect(() => {
    injectStyles();
  }, []);

  // Handle image style selection - prepend style prompt to user's message
  useEffect(() => {
    if (activeToolId === "image_gen" && styleData?.stylePrompt) {
      const stylePrompt = styleData.stylePrompt;
      const currentMessage = message.trim();

      let combinedPrompt;
      if (currentMessage) {
        // Prepend style prompt to existing message
        combinedPrompt = `${stylePrompt}\n\n${currentMessage}`;
      } else {
        // Use style prompt alone
        combinedPrompt = stylePrompt;
      }

      // Update message
      setMessage(combinedPrompt);

      // Focus textarea and position cursor at end
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const length = combinedPrompt.length;
          textareaRef.current.setSelectionRange(length, length);
        }
      }, 50);

      // Clear styleData to prevent re-triggering
      setStyleData(null);
    }
  }, [styleData, activeToolId, message]);

  return (
    <div
      className={cn(
        "relative chat-input-container transition-all duration-500 ease-in-out bg-transparent",
        isNewChat && "-mt-16",
        isMobile && "px-2",
        "z-50",
        files.length > 0 && "has-files"
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {showGreeting && <ChatGreeting key={greetingKey} />}

      <DragDropOverlay
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      />

      <div
        className={cn(
          "relative z-10 transition-all duration-200 rounded-3xl border border-sidebar-border bg-sidebar",
          isFocused &&
            "shadow-[0_0_0_1px_rgba(147,51,234,0.3),0_0_0_4px_rgba(147,51,234,0.1)]",
          isCanvasOpen && "rounded-2xl"
        )}
      >
        <div
          className={cn(
            "flex flex-col",
            "rounded-3xl",
            isCanvasOpen && "rounded-2xl"
          )}
        >
          <FilePreviewList
            files={files}
            onRemoveFile={handleRemoveFile}
            onImageClick={(imageData) => setSelectedImagePreview(imageData)}
            isUploading={isUploading}
          />

          {referencedItems.length > 0 && (
            <div className="px-3 pt-2 flex flex-wrap gap-2">
              {referencedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-1.5 px-2 py-1 bg-sidebar-accent/70 rounded-md border border-sidebar-border text-sm"
                >
                  <span className="text-sidebar-foreground">
                    {item.displayName}
                  </span>
                  <button
                    className="ml-1 text-sidebar-foreground/70 hover:text-sidebar-foreground"
                    onClick={() => removeReferencedItem(item.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Reply context bar */}
          <ReplyContextBar
            replyToMessage={replyToMessage}
            onDismiss={cancelReplyToMessage}
          />

          <div
            className={cn(
              "flex items-center w-full relative",
              isCanvasOpen ? "p-1.5 sm:p-2" : "p-2 sm:p-3"
            )}
          >
            <div className="relative w-full">
              <Textarea
                ref={textareaRef}
                value={message}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={dynamicPlaceholder}
                data-testid={TEST_IDS.CHAT_INPUT}
                className={cn(
                  isCanvasOpen
                    ? "min-h-[28px] sm:min-h-[28px]"
                    : "min-h-[36px] sm:min-h-[36px]",
                  "w-full resize-none border-0 text-base sm:text-sm",
                  "focus-visible:ring-1 focus-visible:ring-purple-400/30 focus-visible:ring-offset-0",
                  "px-3 py-1 pb-8 sm:pb-9 transition-all duration-200",
                  "scrollbar-thumb-rounded scrollbar-thin",
                  "disabled:opacity-70 bg-transparent outline-none",
                  "leading-relaxed overflow-hidden",
                  "text-sidebar-foreground placeholder:text-sidebar-foreground/50",
                  isStreaming ? "opacity-60 cursor-text" : "",
                  "mask-image-bottom",
                  isDeepResearchInProgress
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                )}
                disabled={isDeepResearchInProgress}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                style={{
                  paddingBottom: isCanvasOpen
                    ? "calc(0.5rem + 16px)"
                    : isNarrowToolbar && activeToolId === "image_gen"
                      ? "calc(0.75rem + 72px)" // Two rows (~32px each + gap)
                      : "calc(0.75rem + 18px)", // Single row
                  lineHeight: isMobile ? "18px" : "20px",
                }}
              />
            </div>

            <FileMentionDropdown
              isOpen={mentionDropdownOpen}
              items={filteredItems}
              onSelect={handleItemMentionSelect}
              onClose={() => setMentionDropdownOpen(false)}
              triggerPos={mentionTriggerPos}
              selectedIndex={selectedIndex}
              onMouseEnter={(index) => setSelectedIndex(index)}
              onKeyDown={handleKeyDown}
            />

            {/* Toolbar container - two-row layout at narrow viewports when image_gen active */}
            <div
              className={cn(
                "absolute left-3 sm:left-5 right-3 sm:right-5 z-10",
                isCanvasOpen
                  ? "bottom-1 sm:bottom-1.5"
                  : "bottom-1.5 sm:bottom-2",
                isNarrowToolbar && activeToolId === "image_gen"
                  ? "flex flex-col gap-1.5"
                  : "flex justify-between items-end"
              )}
            >
              {/* Row 1 - Core controls */}
              <div className="flex justify-between items-end w-full">
                <div className="flex items-center space-x-1">
                  <FileUploadButton
                    onClick={
                      disableFileUpload
                        ? () =>
                            onShowAuthOverlay &&
                            onShowAuthOverlay("File Upload")
                        : handleFileChange
                    }
                    disabled={
                      isLoading ||
                      files.length >= maxFiles ||
                      isUploading ||
                      isDeepResearchInProgress
                    }
                    isUploading={isUploading}
                    className="p-1 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    isLimitedMode={isLimitedMode}
                    disableFileUpload={disableFileUpload}
                    onShowAuthOverlay={onShowAuthOverlay}
                  />
                  {!disableVoice && (
                    <VoiceDictationButton
                      onTranscript={(text) => {
                        setMessage((prev) => {
                          // Store original message on first transcript
                          if (
                            !messageBeforeVoiceRef.current &&
                            prev !== undefined
                          ) {
                            messageBeforeVoiceRef.current = prev;
                          }
                          // Set message to original + new transcript
                          const original = messageBeforeVoiceRef.current || "";
                          const trimmedOriginal = original.trim();
                          if (trimmedOriginal) {
                            return trimmedOriginal + " " + text;
                          }
                          return text;
                        });
                      }}
                      onConfirm={() => {
                        // Clear the ref on confirm (keep the transcribed text)
                        messageBeforeVoiceRef.current = "";
                      }}
                      onCancel={() => {
                        // Restore original message on cancel
                        setMessage(messageBeforeVoiceRef.current || "");
                        messageBeforeVoiceRef.current = "";
                      }}
                      disabled={
                        isLoading ||
                        isUploading ||
                        isStreaming ||
                        isDeepResearchInProgress
                      }
                      className="p-1 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    />
                  )}
                  <SimpleToolBox
                    isLimitedMode={isLimitedMode}
                    disableTools={disableTools}
                    disableDeepResearch={disableTools}
                    onShowAuthOverlay={onShowAuthOverlay}
                    onToolChange={setActiveToolId}
                  />
                  {/* Image gen controls - inline at wide viewports only */}
                  {!isNarrowToolbar && activeToolId === "image_gen" && (
                    <>
                      <InlineImageModelSelect />
                      <InlineImageModelParams />
                      <ImageStyleSelector
                        onStyleSelection={(toolId, data) => {
                          setStyleData(data);
                        }}
                        safeT={safeT}
                      />
                    </>
                  )}
                  {activeToolId === "web_search" &&
                    !isDeepResearchEnabled &&
                    !isCanvasModeEnabled && (
                      <WebSearchFocus
                        selectedWebSearchFocus={
                          metadata?.webSearchFocus || "auto"
                        }
                        safeT={safeT}
                      />
                    )}
                </div>

                <div className="flex items-center space-x-1 sm:space-x-1.5">
                  {/* Hide token counter on narrow screens to save space */}
                  {!isStreaming && inputTokens > 0 && !isNarrowToolbar && (
                    <InputTokenCounter
                      tokens={inputTokens}
                      maxTokens={totalTokens}
                      isFocused={isFocused}
                      showError={false}
                    />
                  )}
                  <SendButton
                    isUploading={isUploading}
                    disabled={
                      isDeepResearchInProgress // Disable during deep research
                        ? true
                        : isStreaming
                          ? false
                          : isLoading ||
                            (!message.trim() && files.length === 0) ||
                            isUploading
                    }
                    isEmpty={!message.trim() && files.length === 0}
                    className="p-1"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmitWithNudge(e, {
                        forceWebSearchTool: activeToolId === "web_search",
                        forceImageGenTool: activeToolId === "image_gen",
                        hasExplicitToolSelection: activeToolId !== null,
                        tool_choice: activeToolId ? undefined : "none",
                      });
                    }}
                    isStreaming={isStreaming}
                    onAbort={chatContext?.handleAbortStream}
                  />
                </div>
              </div>

              {/* Row 2 - Image gen controls at narrow viewports */}
              {isNarrowToolbar && activeToolId === "image_gen" && (
                <div className="flex items-center space-x-1.5">
                  <InlineImageModelSelect />
                  <InlineImageModelParams />
                  <ImageStyleSelector
                    onStyleSelection={(toolId, data) => {
                      setStyleData(data);
                    }}
                    safeT={safeT}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ImagePreviewDialog
        selectedImagePreview={selectedImagePreview}
        onClose={() => setSelectedImagePreview(null)}
      />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={Object.entries(ACCEPTED_FILE_TYPES)
          .map(([mime, exts]) =>
            exts.map((ext) => (mime.includes("*") ? `.${ext}` : mime))
          )
          .flat()
          .join(",")}
        onChange={handleFileChange}
        className="hidden"
        disabled={
          isLoading ||
          files.length >= maxFiles ||
          isUploading ||
          disableFileUpload
        }
      />
    </div>
  );
}

ChatInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  maxFiles: PropTypes.number,
  isNewChat: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disableTools: PropTypes.bool,
  disableFileUpload: PropTypes.bool,
  isLimitedMode: PropTypes.bool,
  onShowAuthOverlay: PropTypes.func,
};

export default function WrappedChatInput(props) {
  return (
    <SidebarContextWrapper>
      <ChatInput {...props} />
    </SidebarContextWrapper>
  );
}
